import { useLazyQuery, useMutation } from "@apollo/client";
import { Button, Form, message, Modal, Space, Table } from "antd";
import dayjs from "dayjs";
import React, { useState } from "react";
import { translate } from "../../../../core";
import { InputCard } from "../../../inventory/component/InputCard";
import { GetItemsForPage } from "../../../inventory/graphql/item.graphql";
import { MassChangeMutation } from "../../graphql/mass-change.graphql";
import styles from "../MassChange.module.scss";
import { useCategoryQueries } from "./CategoryQueries";
import MassChangeForm from "./MassChangeForm";
import SearchModal from "./SearchModal";
import { getTableColumns } from "./tableColumns";
import { EnabledFields, MassChangeRequest, TableItem, UpdateSelectionMode } from "./types";
import { ItemFindRequest } from "../../../inventory/type/item-query";

const MassChange: React.FC = () => {
  // State
  const [searchOpen, setSearchOpen] = useState(false);
  const [tableData, setTableData] = useState<TableItem[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [excludedItemIds, setExcludedItemIds] = useState<number[]>([]);
  const [includedItemIds, setIncludedItemIds] = useState<number[]>([]);
  const [form] = Form.useForm();
  const [lastSearchRequest, setLastSearchRequest] =
    useState<MassChangeRequest | null>(null);
  const [updateSelectionMode, setUpdateSelecionMode] = useState<UpdateSelectionMode>()
  const [confirmModalVisible, setConfirmModalVisible] = useState(false);

  // Category state
  const [categoryId, setCategoryId] = useState<number | null>(null);
  const [subcategoryId, setSubcategoryId] = useState<number | null>(null);
  const [subcategory2Id, setSubcategory2Id] = useState<number | null>(null);

  // Enabled fields state
  const [enabledFields, setEnabledFields] = useState<EnabledFields>({
    categoryId: false,
    subcategoryId: false,
    subcategory2Id: false,
    showOnWeb: false,
    saleStartDate: false,
    saleEndDate: false,
    nonPurchasable: false,
    nonPurchasablePriceEnabled: false,
  });

  // Queries and Mutations
  const [find, { loading }] = useLazyQuery(GetItemsForPage);
  const [massChangeItems, { loading: submitLoading }] =
    useMutation(MassChangeMutation);

  // Category queries
  const {
    categoryOptions,
    subcategoryOptions,
    subcategory2Options,
    refreshAllCategories,
  } = useCategoryQueries({
    categoryId,
    subcategoryId,
  });

  const handleTableChange = async (pagination: any) => {
    // If page size changes, reset to page 1
    if (pagination.pageSize !== pageSize) {
      setCurrentPage(1);
      setPageSize(pagination.pageSize);

      // Trigger a new search with the updated pagination
      const { data: result } = await find({
        variables: {
          request: { 
            skip: 0, // Reset to first page
            take: (pagination.pageSize ?? 20) as number,
            archived: false,
            ...(lastSearchRequest as ItemFindRequest),
          },
        },
      });
      if (result?.itemsFind?.data) {
        const newItems: TableItem[] = result.itemsFind.data.map(
          (item: any) => ({
            id: item.id,
            key: item.sku,
            sku: item.sku,
            type: item.type || "",
            category: item.category?.title || "",
            description: item.description || "",
            price: item.oursPrice,
            salePeriod: item.saleStartDate ? `${dayjs(item.saleStartDate).format("YYYY-MM-DD")}  ${dayjs(item.saleEndDate).format("YYYY-MM-DD")}` : "",
            vendor: item.vendor?.contactId || "",
            style: item.style || "",
            patternNumber: item.patternNumber || "",
            showOnWeb: item.showOnline || false,
            nonPurchasable: item.nonPurchasable || false,
            nonPurchasablePriceEnabled: item.nonPurchasablePriceEnabled || false,
            enabled: true,
          } as TableItem)
        );
        setTableData(newItems);
        if (result.itemsFind.count) {
          setTotalCount(result.itemsFind.count);
        }
      }
    } else {
      // Normal page change
      setCurrentPage(pagination.current);
      setPageSize(pagination.pageSize);
      // Trigger a new search with the updated pagination
      const { data: result } = await find({
        variables: {
          request: {
            skip: (pagination.current - 1) * pagination.pageSize,
            take: (pagination.pageSize ?? 20) as number,
            archived: false,
            ...lastSearchRequest as ItemFindRequest,
          },
        },
      });

      if (result?.itemsFind?.data) {
        const newItems: TableItem[] = result.itemsFind.data.map(
          (item: any) => ({
            id: item.id,
            key: item.sku,
            sku: item.sku,
            type: item.type || "",
            category: item.category?.title || "",
            description: item.description || "",
            price: item.oursPrice,
            salePeriod: item.saleStartDate ? `${dayjs(item.saleStartDate).format("YYYY-MM-DD")}  ${dayjs(item.saleEndDate).format("YYYY-MM-DD")}` : "",
            vendor: item.vendor?.contactId || "",
            style: item.style || "",
            patternNumber: item.patternNumber || "",
            showOnWeb: item.showOnline || false,
            enabled: true,
          } as TableItem)
        );
        setTableData(newItems);
        if (result.itemsFind.count) {
          setTotalCount(result.itemsFind.count);
        }
      }
    }
  };

  // Form validation
  const isFormValid = () => {
    // Check if there are any enabled fields with values
    const hasEnabledFields = Object.values(enabledFields).some(
      (value) => value === true
    );

    // Check if there are any items in the table
    const hasTableItems = tableData.length > 0;

    // Check if there are any enabled items
    const hasEnabledItems = tableData.some((item) => item.enabled);

    // Check if there's a search request
    const hasSearchRequest = !!lastSearchRequest;

    // Return true only if all conditions are met
    return (
      hasEnabledFields && hasTableItems && hasEnabledItems && hasSearchRequest
    );
  };

  // Show confirmation modal
  const showConfirmModal = async () => {
    try {
      await form.validateFields();
      setConfirmModalVisible(true);
    } catch (error) {
      // Form validation failed
    }
  };

  // Form submission
  const handleFormSubmit = async () => {
    try {
      const values = await form.validateFields();
      const selectedChanges = [];

      if (enabledFields.categoryId) {
        selectedChanges.push({
          name: "categoryId",
          value: values.categoryId.toString(),
        });
      }

      if (enabledFields.subcategoryId) {
        selectedChanges.push({
          name: "subcategoryId",
          value: values.subcategoryId.toString(),
        });
      }

      if (enabledFields.subcategory2Id) {
        selectedChanges.push({
          name: "subcategory2Id",
          value: values.subcategory2Id.toString(),
        });
      }

      if (enabledFields.saleStartDate) {
        selectedChanges.push({
          name: "saleStartDate",
          value: values.saleStartDate?.format("YYYY-MM-DD"),
        });
      }

      if (enabledFields.saleEndDate) {
        selectedChanges.push({
          name: "saleEndDate",
          value: values.saleEndDate?.format("YYYY-MM-DD"),
        });
      }

      if (enabledFields.showOnWeb) {
        selectedChanges.push({
          name: "showOnWeb",
          value: values.showOnWeb.toString(),
        });
      }

      if (enabledFields.nonPurchasable) {
        selectedChanges.push({
          name: "nonPurchasable",
          value: [values.nonPurchasable.toString(), values.nonPurchasablePriceEnabled?.toString()].join('|'),
        });
      }

      const input = {
        search: updateSelectionMode === UpdateSelectionMode.Automatic ? lastSearchRequest : null,
        include: includedItemIds,
        exclude: excludedItemIds,
        data: selectedChanges,
      };

      const { data } = await massChangeItems({
        variables: {
          input,
        },
      });

      setConfirmModalVisible(false);

      if (data) {
        message.success("Changes applied successfully");

        // Reset all states
        form.resetFields();
        form.setFieldsValue({
          saleStartDate: null,
          saleEndDate: null,
        });
        setTableData([]);
        setCurrentPage(1);
        setPageSize(10);
        setTotalCount(0);
        setExcludedItemIds([]);
        setLastSearchRequest(null);
        setCategoryId(null);
        setSubcategoryId(null);
        setSubcategory2Id(null);
        setEnabledFields({
          categoryId: false,
          subcategoryId: false,
          subcategory2Id: false,
          showOnWeb: false,
          saleStartDate: false,
          saleEndDate: false,
          nonPurchasable: false,
          nonPurchasablePriceEnabled: false,
        });
      }
    } catch (error) {
      setConfirmModalVisible(false);
      message.error("Failed to apply changes");
    }
  };

  // Search completion handler
  const onCompleteSearch = async (
    request: MassChangeRequest | null | undefined,
    mode: UpdateSelectionMode
  ) => {
    setSearchOpen(false);
    setExcludedItemIds([])
    setIncludedItemIds([])

    // Store the search request for future use
    if (request) {
      setLastSearchRequest(request);
    }

    setUpdateSelecionMode(mode)

    // If there's a table search SKU, add it to the request
    const searchRequest = request ? { ...request } : {};

    const { data: result } = await find({
      variables: {
        request: {
          archived: false,
          skip: (currentPage - 1) * pageSize,
          take: pageSize as number,
          ...(searchRequest as ItemFindRequest),
        },
      },
    });

    if (result?.itemsFind?.data) {
      const newItems: TableItem[] = result.itemsFind.data.map((item: any) => ({
        id: item.id,
        key: item.sku,
        sku: item.sku,
        type: item.type || "",
        category: item.category?.title || "",
        description: item.description || "",
        price: item.oursPrice,
        salePeriod: item.saleStartDate ? `${dayjs(item.saleStartDate).format("YYYY-MM-DD")}  ${dayjs(item.saleEndDate).format("YYYY-MM-DD")}` : "",
        vendor: item.vendor?.contactId || "",
        style: item.style || "",
        patternNumber: item.patternNumber || "",
        showOnWeb: item.showOnline || false,
        enabled: true,
      } as TableItem));
      setTableData(newItems); // Replace existing data instead of appending

      // Update pagination with the total count from the query
      if (result.itemsFind.count) {
        setTotalCount(result.itemsFind.count);
      }
    }
  };

  // Render
  return (
    <>
      <Button
        onClick={() => setSearchOpen(true)}
        className={`${styles.searchButton} btn-primary-gradient`}
        loading={loading}
      >
        {translate('find', 'products')}
      </Button>
      <InputCard contained>
        <Space direction="vertical" style={{ width: "100%" }}>
          <Table
            loading={loading}
            columns={getTableColumns({
              tableData,
              setTableData,
              excludedItemIds,
              setExcludedItemIds,
              includedItemIds,
              setIncludedItemIds,
              updateSelectionMode: updateSelectionMode || UpdateSelectionMode.Automatic
              })}
            dataSource={tableData}
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: totalCount,
              showSizeChanger: true,
              showTotal: (total) => translate("total", total.toString()),
            }}
            onChange={handleTableChange}
            rowKey="key"
            className={styles.tableContainer}
          />

          <InputCard contained>
            <MassChangeForm
              form={form}
              enabledFields={enabledFields}
              setEnabledFields={setEnabledFields}
              categoryId={categoryId}
              setCategoryId={setCategoryId}
              subcategoryId={subcategoryId}
              setSubcategoryId={setSubcategoryId}
              subcategory2Id={subcategory2Id}
              setSubcategory2Id={setSubcategory2Id}
              categoryOptions={categoryOptions}
              subcategoryOptions={subcategoryOptions}
              subcategory2Options={subcategory2Options}
              isFormValid={isFormValid}
              massChangeLoading={loading || submitLoading}
              handleFormSubmit={showConfirmModal}
              refreshCategories={refreshAllCategories}
            />
          </InputCard>
        </Space>
      </InputCard>

      {/* Search Modal */}
      <SearchModal
        searchOpen={searchOpen}
        setSearchOpen={setSearchOpen}
        onCompleteSearch={onCompleteSearch}
        loading={loading}
        enabledFields={enabledFields}
        form={form}
      />

      {/* Confirmation Modal */}
      <Modal
        title={translate("confirmation")}
        open={confirmModalVisible}
        onOk={handleFormSubmit}
        onCancel={() => setConfirmModalVisible(false)}
        confirmLoading={submitLoading}
        okText={translate("confirm")}
        cancelText={translate("cancel")}
      >
        <p>{translate("massChangeConfirmation")}</p>
      </Modal>
    </>
  );
};

export default MassChange;
